import React from "react";
import * as Dialog from "@radix-ui/react-dialog";
import {X} from "lucide-react";
import {cn} from "../../utils/cn";

const sizeMap = {
  sm: "max-w-sm",
  md: "max-w-md",
  lg: "max-w-lg",
  xl: "max-w-xl",
};

/**
 * Centered modal dialog. Use for confirmations and ≤3-field forms.
 * For longer forms, use SlideOver.
 */
function Modal({open, onOpenChange, title, subtitle, size = "md", children, className}) {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay
          className={cn(
            "fixed inset-0 bg-ink-900/40 backdrop-blur-[2px] z-modal",
            "data-[state=open]:animate-fade-in",
            "data-[state=closed]:animate-fade-out"
          )}
        />
        <Dialog.Content
          className={cn(
            "fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-modal",
            "w-[calc(100vw-2rem)] bg-white rounded-2xl shadow-xl",
            "focus:outline-none",
            "data-[state=open]:animate-scale-in",
            sizeMap[size],
            className
          )}
        >
          <div className="px-6 pt-5 pb-4 border-b border-ink-100">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                {title && <Dialog.Title className="text-lg font-semibold text-ink-900">{title}</Dialog.Title>}
                {subtitle && (
                  <Dialog.Description className="text-sm text-ink-500 mt-0.5">{subtitle}</Dialog.Description>
                )}
              </div>
              <Dialog.Close asChild>
                <button
                  type="button"
                  aria-label="Close"
                  className="inline-flex items-center justify-center h-8 w-8 rounded-md text-ink-500 hover:bg-ink-100 hover:text-ink-700 focus-visible:ring"
                >
                  <X className="h-4 w-4" />
                </button>
              </Dialog.Close>
            </div>
          </div>
          {children}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

Modal.Body = function ModalBody({className, children, ...props}) {
  return (
    <div className={cn("px-6 py-5", className)} {...props}>
      {children}
    </div>
  );
};

Modal.Footer = function ModalFooter({className, children, ...props}) {
  return (
    <div
      className={cn("px-6 py-4 border-t border-ink-100 flex items-center justify-end gap-2", className)}
      {...props}
    >
      {children}
    </div>
  );
};

export default Modal;
